import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, Spinner } from 'react-bootstrap';
import { useMutation, useLazyQuery } from '@apollo/client';
import { GENERATE_PAYSLIP, GET_ATTENDANCE_SUMMARY } from '../../graphql/operations';

interface User {
  id: string;
  username: string;
  role: string;
}

interface PayslipModalProps {
  show: boolean;
  onHide: () => void;
  onSuccess: (message: string) => void;
  users: User[];
}

const months = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

const currentYear = new Date().getFullYear();
const years = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i); // 2 years back, current year, 2 years forward

const PayslipModal: React.FC<PayslipModalProps> = ({ show, onHide, onSuccess, users }) => {
  const [generatePayslip, { loading: generating }] = useMutation(GENERATE_PAYSLIP);
  const [getAttendanceSummary, { data: attendanceData, loading: loadingAttendance }] = useLazyQuery(GET_ATTENDANCE_SUMMARY, {
    fetchPolicy: 'network-only'
  });

  const staffUsers = users.filter((u) => u.role !== 'customer');

  // Form Fields
  const [userId, setUserId] = useState('');
  const [month, setMonth] = useState<number>(new Date().getMonth() + 1);
  const [year, setYear] = useState<number>(currentYear);
  const [baseSalary, setBaseSalary] = useState<number>(20000);
  const [allowances, setAllowances] = useState<number>(0);
  const [deductions, setDeductions] = useState<number>(0);
  const [tax, setTax] = useState<number>(0);
  const [presentDays, setPresentDays] = useState<number>(0);
  const [workingDays, setWorkingDays] = useState<number>(30);
  const [status, setStatus] = useState<'Paid' | 'Pending'>('Paid');
  const [paymentMode, setPaymentMode] = useState<'Bank Transfer' | 'Cash' | 'Cheque' | 'UPI'>('Bank Transfer');
  const [notes, setNotes] = useState('');

  // Errors
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState('');

  // Auto-calculated Net Salary
  const netSalary = baseSalary + allowances - deductions - tax;

  // Trigger attendance summary lookup when employee, month, or year changes
  useEffect(() => {
    if (show && userId && month && year) {
      const lastDay = new Date(year, month, 0).getDate();
      setWorkingDays(lastDay);
      
      const startDate = `${year}-${String(month).padStart(2, '0')}-01`;
      const endDate = `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
      
      getAttendanceSummary({
        variables: { userId, startDate, endDate }
      });
    }
  }, [show, userId, month, year, getAttendanceSummary]);

  // Set suggested present days from attendance lookup
  useEffect(() => {
    if (attendanceData?.attendanceSummary) {
      const summary = attendanceData.attendanceSummary;
      // present counts as 1, half_day counts as 0.5
      const calculatedPresent = summary.presentCount + (summary.halfDayCount * 0.5);
      setPresentDays(calculatedPresent);
      
      // Suggest basic salary modification based on present days if desired (e.g., prorated)
      // Let's keep it manual but show visual indicator or let admin decide.
    } else {
      setPresentDays(0);
    }
  }, [attendanceData]);

  // Reset form when modal opens
  useEffect(() => {
    if (show) {
      setUserId(staffUsers[0]?.id || '');
      setMonth(new Date().getMonth() + 1);
      setYear(currentYear);
      setBaseSalary(20000);
      setAllowances(0);
      setDeductions(0);
      setTax(0);
      setPresentDays(0);
      setStatus('Paid');
      setPaymentMode('Bank Transfer');
      setNotes('');
      setErrors({});
      setSubmitError('');
    }
  }, [show]);

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!userId) {
      newErrors.userId = 'Please select an employee';
    }
    if (baseSalary < 0) {
      newErrors.baseSalary = 'Base salary must be positive';
    }
    if (allowances < 0) {
      newErrors.allowances = 'Allowances must be positive or zero';
    }
    if (deductions < 0) {
      newErrors.deductions = 'Deductions must be positive or zero';
    }
    if (tax < 0) {
      newErrors.tax = 'Tax must be positive or zero';
    }
    if (presentDays < 0 || presentDays > workingDays) {
      newErrors.presentDays = `Present days must be between 0 and ${workingDays}`;
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitError('');

    try {
      const { data } = await generatePayslip({
        variables: {
          userId,
          month: parseInt(String(month)),
          year: parseInt(String(year)),
          baseSalary: parseFloat(String(baseSalary)),
          allowances: parseFloat(String(allowances)),
          deductions: parseFloat(String(deductions)),
          tax: parseFloat(String(tax)),
          presentDays: parseFloat(String(presentDays)),
          workingDays: parseFloat(String(workingDays)),
          status,
          paymentMode,
          notes,
        },
      });

      if (data?.generatePayslip?.success) {
        onSuccess(data.generatePayslip.message || 'Payslip generated successfully!');
        onHide();
      } else {
        setSubmitError(data?.generatePayslip?.message || 'Failed to generate payslip.');
      }
    } catch (err: any) {
      setSubmitError(err.message || 'An error occurred during submission.');
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered scrollable>
      <Modal.Header closeButton className="border-bottom pb-3" style={{ background: 'linear-gradient(135deg, #f8f9ff, #ffffff)' }}>
        <Modal.Title className="fw-bold text-dark" style={{ fontSize: '18px' }}>
          <i className="fas fa-money-check-alt text-primary me-2" />
          Generate Employee Payslip
        </Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body style={{ padding: '24px' }}>
          {submitError && (
            <div className="alert alert-danger mb-4 py-2 px-3 rounded-3 border-0" style={{ fontSize: '13px' }}>
              <i className="fas fa-exclamation-triangle me-2" />
              {submitError}
            </div>
          )}

          <Row className="g-3">
            {/* Period and Employee Info */}
            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-semibold text-dark" style={{ fontSize: '13px' }}>Select Employee *</Form.Label>
                <Form.Select
                  value={userId}
                  onChange={(e) => setUserId(e.target.value)}
                  style={{ borderRadius: '8px', fontSize: '13px' }}
                  isInvalid={!!errors.userId}
                >
                  <option value="">-- Choose Staff member --</option>
                  {staffUsers.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.username} ({u.role})
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">{errors.userId}</Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={3}>
              <Form.Group>
                <Form.Label className="fw-semibold text-dark" style={{ fontSize: '13px' }}>Month *</Form.Label>
                <Form.Select
                  value={month}
                  onChange={(e) => setMonth(parseInt(e.target.value))}
                  style={{ borderRadius: '8px', fontSize: '13px' }}
                >
                  {months.map((m) => (
                    <option key={m.value} value={m.value}>
                      {m.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={3}>
              <Form.Group>
                <Form.Label className="fw-semibold text-dark" style={{ fontSize: '13px' }}>Year *</Form.Label>
                <Form.Select
                  value={year}
                  onChange={(e) => setYear(parseInt(e.target.value))}
                  style={{ borderRadius: '8px', fontSize: '13px' }}
                >
                  {years.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>

            {/* Attendance Integration Alert/Indicator */}
            {userId && (
              <Col md={12}>
                <div className="bg-light border rounded-3 p-3 d-flex align-items-center justify-content-between">
                  <div>
                    <span className="fw-bold text-dark d-block" style={{ fontSize: '12.5px' }}>
                      <i className="fas fa-calendar-check text-primary me-2" />
                      Attendance Synchronization
                    </span>
                    <span className="text-muted" style={{ fontSize: '11.5px' }}>
                      {loadingAttendance ? (
                        <>Fetching attendance records...</>
                      ) : (
                        <>
                          Found <strong>{attendanceData?.attendanceSummary?.presentCount || 0} present</strong>,{' '}
                          <strong>{attendanceData?.attendanceSummary?.halfDayCount || 0} half days</strong> in selected period.
                        </>
                      )}
                    </span>
                  </div>
                  {loadingAttendance && <Spinner size="sm" animation="border" className="text-primary ms-3" />}
                </div>
              </Col>
            )}

            {/* Base Financial Attributes */}
            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-semibold text-dark" style={{ fontSize: '13px' }}>Base Monthly Salary (₹) *</Form.Label>
                <Form.Control
                  type="number"
                  placeholder="20000"
                  value={baseSalary || ''}
                  onChange={(e) => setBaseSalary(parseFloat(e.target.value) || 0)}
                  style={{ borderRadius: '8px', fontSize: '13px' }}
                  isInvalid={!!errors.baseSalary}
                />
                <Form.Control.Feedback type="invalid">{errors.baseSalary}</Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-semibold text-dark" style={{ fontSize: '13px' }}>Allowances / Bonuses (₹)</Form.Label>
                <Form.Control
                  type="number"
                  placeholder="0"
                  value={allowances || ''}
                  onChange={(e) => setAllowances(parseFloat(e.target.value) || 0)}
                  style={{ borderRadius: '8px', fontSize: '13px' }}
                  isInvalid={!!errors.allowances}
                />
                <Form.Control.Feedback type="invalid">{errors.allowances}</Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-semibold text-dark" style={{ fontSize: '13px' }}>Deductions / Loss of Pay (₹)</Form.Label>
                <Form.Control
                  type="number"
                  placeholder="0"
                  value={deductions || ''}
                  onChange={(e) => setDeductions(parseFloat(e.target.value) || 0)}
                  style={{ borderRadius: '8px', fontSize: '13px' }}
                  isInvalid={!!errors.deductions}
                />
                <Form.Control.Feedback type="invalid">{errors.deductions}</Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-semibold text-dark" style={{ fontSize: '13px' }}>Income Tax / PF Deduction (₹)</Form.Label>
                <Form.Control
                  type="number"
                  placeholder="0"
                  value={tax || ''}
                  onChange={(e) => setTax(parseFloat(e.target.value) || 0)}
                  style={{ borderRadius: '8px', fontSize: '13px' }}
                  isInvalid={!!errors.tax}
                />
                <Form.Control.Feedback type="invalid">{errors.tax}</Form.Control.Feedback>
              </Form.Group>
            </Col>

            {/* Attendance Stats Override */}
            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-semibold text-dark" style={{ fontSize: '13px' }}>Present Days *</Form.Label>
                <Form.Control
                  type="number"
                  step="0.5"
                  value={presentDays}
                  onChange={(e) => setPresentDays(parseFloat(e.target.value) || 0)}
                  style={{ borderRadius: '8px', fontSize: '13px' }}
                  isInvalid={!!errors.presentDays}
                />
                <Form.Control.Feedback type="invalid">{errors.presentDays}</Form.Control.Feedback>
                <small className="text-muted" style={{ fontSize: '11px' }}>Auto-suggested from attendance sheet.</small>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-semibold text-dark" style={{ fontSize: '13px' }}>Working Days in Month *</Form.Label>
                <Form.Control
                  type="number"
                  value={workingDays}
                  onChange={(e) => setWorkingDays(parseInt(e.target.value) || 0)}
                  style={{ borderRadius: '8px', fontSize: '13px' }}
                />
              </Form.Group>
            </Col>

            {/* Mode & Status */}
            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-semibold text-dark" style={{ fontSize: '13px' }}>Payment Mode</Form.Label>
                <Form.Select
                  value={paymentMode}
                  onChange={(e) => setPaymentMode(e.target.value as any)}
                  style={{ borderRadius: '8px', fontSize: '13px' }}
                >
                  <option value="Bank Transfer">Bank Transfer</option>
                  <option value="Cash">Cash</option>
                  <option value="Cheque">Cheque</option>
                  <option value="UPI">UPI</option>
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-semibold text-dark" style={{ fontSize: '13px' }}>Payment Status</Form.Label>
                <div className="d-flex gap-2">
                  {(['Paid', 'Pending'] as const).map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setStatus(s)}
                      className={`btn py-2 px-3 flex-fill border ${status === s ? 'btn-primary border-primary fw-semibold' : 'btn-outline-secondary bg-white text-muted'}`}
                      style={{ borderRadius: '8px', fontSize: '13px' }}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </Form.Group>
            </Col>

            {/* Notes */}
            <Col md={12}>
              <Form.Group>
                <Form.Label className="fw-semibold text-dark" style={{ fontSize: '13px' }}>Notes / Remarks</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  placeholder="e.g. Salary credited via bank transfer, includes festival bonus..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  style={{ borderRadius: '8px', fontSize: '13px' }}
                />
              </Form.Group>
            </Col>

            {/* Net Salary Highlight */}
            <Col md={12} className="mt-3">
              <div className="bg-primary bg-opacity-10 border border-primary border-opacity-25 rounded-4 p-3 d-flex align-items-center justify-content-between">
                <div>
                  <span className="text-primary fw-bold d-block" style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Calculated Net Salary
                  </span>
                  <span className="text-muted" style={{ fontSize: '11px' }}>
                    Base + Allowances - Deductions - Tax
                  </span>
                </div>
                <div className="text-primary fw-bold" style={{ fontSize: '24px' }}>
                  ₹{netSalary.toLocaleString('en-IN')}
                </div>
              </div>
            </Col>
          </Row>
        </Modal.Body>

        <Modal.Footer className="border-top">
          <Button variant="outline-secondary" onClick={onHide} disabled={generating}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={generating}>
            {generating ? (
              <><Spinner size="sm" animation="border" className="me-2" />Generating...</>
            ) : (
              <><i className="fas fa-plus-circle me-2" />Generate Payslip</>
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default PayslipModal;
